import { create } from 'zustand'
import type { Ingredient } from '../types/ingredient'
import type { PaginatedResponse } from '../types/common'
import { IngredientsService, type IngredientsFilters } from '../services/api/ingredientsService'

interface IngredientsState {
  ingredients: PaginatedResponse<Ingredient> | null
  currentIngredient: Ingredient | null
  isLoading: boolean
  error: string | null
  filters: IngredientsFilters
  
  // Actions
  fetchIngredients: () => Promise<void>
  fetchIngredient: (id: string) => Promise<void>
  createIngredient: (ingredient: Partial<Ingredient>) => Promise<void>
  updateIngredient: (id: string, ingredient: Partial<Ingredient>) => Promise<void>
  deleteIngredient: (id: string) => Promise<void>
  setFilters: (filters: Partial<IngredientsFilters>) => void
  resetFilters: () => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  invalidateCache: () => void
}

const defaultFilters: IngredientsFilters = {
  search: '',
  toValidate: null,
  page: 1,
  limit: 20,
  sortBy: 'inciName',
}

export const useIngredientsStore = create<IngredientsState>()((set, get) => ({
  ingredients: null,
  currentIngredient: null,
  isLoading: false,
  error: null,
  filters: defaultFilters,

  setLoading: (loading: boolean) => {
    set({ isLoading: loading })
  },

  setError: (error: string | null) => {
    set({ error })
  },

  setFilters: (newFilters: Partial<IngredientsFilters>) => {
    const currentFilters = get().filters
    const updatedFilters = { ...currentFilters, ...newFilters }
    set({ filters: updatedFilters })
    // Don't fetch automatically - let the component decide when to fetch
  },

  resetFilters: () => {
    set({ filters: defaultFilters })
    // Don't fetch automatically - let the component decide when to fetch
  },

  fetchIngredients: async () => {
    const { filters, setLoading, setError } = get()
    
    try {
      setLoading(true)
      setError(null)
      
      const response = await IngredientsService.getIngredients(filters)
      
      set({ 
        ingredients: {
          data: response.data,
          currentPage: response.currentPage,
          firstPage: response.firstPage,
          lastPage: response.lastPage,
          pageSize: response.pageSize,
          totalRecords: response.totalRecords,
        }
      })
    } catch (error) {
      console.error('Error fetching ingredients:', error)
      setError('Erreur lors du chargement des ingrédients')
    } finally {
      setLoading(false)
    }
  },

  fetchIngredient: async (id: string) => {
    const { setLoading, setError } = get()
    
    try {
      setLoading(true)
      setError(null)
      
      const ingredient = await IngredientsService.getIngredient(id)
      set({ currentIngredient: ingredient })
    } catch (error) {
      console.error('Error fetching ingredient:', error)
      setError('Erreur lors du chargement de l\'ingrédient')
    } finally {
      setLoading(false)
    }
  },

  createIngredient: async (ingredient: Partial<Ingredient>) => {
    const { setLoading, setError, fetchIngredients } = get()
    
    try {
      setLoading(true)
      setError(null)
      
      await IngredientsService.createIngredient(ingredient)
      await fetchIngredients()
    } catch (error) {
      console.error('Error creating ingredient:', error)
      setError('Erreur lors de la création de l\'ingrédient')
      throw error
    } finally {
      setLoading(false)
    }
  },

  updateIngredient: async (id: string, ingredient: Partial<Ingredient>) => {
    const { setLoading, setError, fetchIngredients } = get()
    
    try {
      setLoading(true)
      setError(null)
      
      await IngredientsService.updateIngredient({ ...ingredient, uuid: id })
      await fetchIngredients()
    } catch (error) {
      console.error('Error updating ingredient:', error)
      setError('Erreur lors de la mise à jour de l\'ingrédient')
      throw error
    } finally {
      setLoading(false)
    }
  },

  deleteIngredient: async (id: string) => {
    const { setLoading, setError, fetchIngredients } = get()
    
    try {
      setLoading(true)
      setError(null)
      
      await IngredientsService.deleteIngredient(id)
      await fetchIngredients()
    } catch (error) {
      console.error('Error deleting ingredient:', error)
      setError('Erreur lors de la suppression de l\'ingrédient')
      throw error
    } finally {
      setLoading(false)
    }
  },

  invalidateCache: () => {
    // Reset ingredients to null to force a refetch next time the list is displayed
    set({ ingredients: null })
  },
}))